import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/AuthContext";
import AppShell, { PageContainer } from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Flame, TrendingUp, Trophy, Skull, Star, ArrowUpRight, Bot } from "lucide-react";
import { toast } from "sonner";

const TREND_LABEL = {
  zombies_hot: { label: "Zombies Hot", Icon: Skull, cls: "bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300" },
  meta_hot: { label: "Meta Hot", Icon: Trophy, cls: "bg-amber-500/15 border-amber-500/30 text-amber-300" },
  trending: { label: "Trending", Icon: Flame, cls: "bg-rose-500/15 border-rose-500/30 text-rose-300" },
  rising: { label: "Rising", Icon: TrendingUp, cls: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" },
};

export default function HubIndexPage() {
  const { lang } = useT();
  const { user } = useAuth();
  const isDE = (lang || "de").startsWith("de");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState(new Set());

  useEffect(() => {
    // SEO head (static index — no slug-specific call)
    const orig = document.title;
    document.title = isDE
      ? "Game Hubs · Live Meta, Patchnotes & Builds für 18 Spiele · FicoPilot"
      : "Game Hubs · Live Meta, Patch Notes & Builds for 18 games · FicoPilot";

    const desc = isDE
      ? "Aktuelle Meta, Patchnotes, Top-Builds und Tipps für Valorant, CS2, Warzone, Zombies, LoL, Apex, Battlefield, Elden Ring und mehr – live von FicoPilot AI."
      : "Live meta, patch notes, top builds and tips for Valorant, CS2, Warzone, Zombies, LoL, Apex, Battlefield, Elden Ring and more — powered by FicoPilot AI.";
    let m = document.head.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
    m.setAttribute("content", desc);

    function setMeta(name, value, isProp = false) {
      const attr = isProp ? "property" : "name";
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", value);
    }
    setMeta("og:title", document.title, true);
    setMeta("og:description", desc, true);
    setMeta("og:url", "https://ficopilot.app/hub", true);
    setMeta("og:image", "https://ficopilot.app/hubs/valorant.jpg", true);
    setMeta("og:type", "website", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", document.title);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", "https://ficopilot.app/hubs/valorant.jpg");
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
    link.setAttribute("href", "https://ficopilot.app/hub");

    return () => { document.title = orig; };
  }, [isDE]);

  useEffect(() => {
    api.get("/hub").then((r) => {
      setItems(r.data?.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/me/favorites/hubs").then((r) => {
      setFavorites(new Set((r.data?.items || []).map((i) => i.slug)));
    }).catch(() => {});
  }, [user]);

  const toggleFav = async (slug, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.message(isDE ? "Anmelden, um zu favorisieren" : "Sign in to favorite");
      return;
    }
    if (favorites.has(slug)) {
      try {
        await api.delete(`/me/favorites/hubs/${slug}`);
        setFavorites((s) => { const n = new Set(s); n.delete(slug); return n; });
      } catch { /* noop */ }
    } else {
      try {
        await api.post("/me/favorites/hubs", { slug });
        setFavorites((s) => new Set(s).add(slug));
        toast.success(isDE ? "Zu Favoriten hinzugefügt" : "Added to favorites");
      } catch { /* noop */ }
    }
  };

  return (
    <AppShell>
      <PageContainer className="!max-w-6xl !px-0">
        <section className="relative overflow-hidden border-b border-white/5 bg-gradient-to-b from-zinc-950 via-zinc-950 to-zinc-900 rounded-3xl mb-8">
          <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,rgba(0,229,255,0.18),transparent_40%),radial-gradient(circle_at_80%_70%,rgba(255,70,85,0.18),transparent_40%)]" />
          <div className="relative px-5 sm:px-8 py-12 sm:py-16">
            <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-neon">
              {isDE ? "Live · KI-gestützte Guides" : "Live · AI-powered guides"}
            </span>
            <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight mt-2" data-testid="hub-index-title">
              {isDE ? "Game Hubs" : "Game Hubs"}
            </h1>
            <p className="mt-3 max-w-2xl text-zinc-300 text-base sm:text-lg">
              {isDE
                ? "Aktuelle Meta, Patchnotes, Top-Builds und Strategien für 18 Spiele — live generiert und stündlich aktualisierbar."
                : "Current meta, patch notes, top builds and strategies for 18 games — generated live and hourly refreshable."}
            </p>
            <div className="mt-5 flex gap-2 flex-wrap">
              <Link to="/app/coach">
                <Button className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold h-11 px-5">
                  <Bot size={15} className="mr-2" /> {isDE ? "AI-Coach öffnen" : "Open AI Coach"}
                </Button>
              </Link>
              <Link to="/app/zombies">
                <Button variant="outline" className="rounded-full bg-transparent border-white/15 text-white hover:bg-white/5 hover:text-white h-11 px-5">
                  <Skull size={15} className="mr-2" /> {isDE ? "Zombies Hub" : "Zombies Hub"}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="px-5 sm:px-0 pb-8">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-white/5 bg-zinc-900/50 h-64 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="hub-index-grid">
              {items.map((h, i) => {
                const trend = h.trending ? TREND_LABEL[h.trending] : null;
                const TIcon = trend?.Icon;
                const isFav = favorites.has(h.slug);
                return (
                  <Link
                    key={h.slug}
                    to={`/hub/${h.slug}`}
                    data-testid={`hub-card-${h.slug}`}
                    className="group relative rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/60 hover:border-neon/40 hover:shadow-neon transition-all animate-fade-up"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={h.cover}
                        alt={h.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
                      {trend && TIcon && (
                        <Badge className={`absolute top-3 left-3 inline-flex items-center gap-1 ${trend.cls}`} data-testid={`hub-card-${h.slug}-trend`}>
                          <TIcon size={11} /> {trend.label}
                        </Badge>
                      )}
                      <button
                        type="button"
                        onClick={(e) => toggleFav(h.slug, e)}
                        data-testid={`hub-card-${h.slug}-fav`}
                        className={`absolute top-3 right-3 w-9 h-9 rounded-full grid place-items-center transition-colors backdrop-blur ${
                          isFav
                            ? "bg-neon text-black"
                            : "bg-black/40 text-zinc-200 hover:bg-neon/20 hover:text-neon border border-white/10"
                        }`}
                        aria-label={isDE ? "Favorit umschalten" : "Toggle favorite"}
                      >
                        <Star size={15} fill={isFav ? "currentColor" : "none"} />
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-display text-lg font-bold leading-tight">{h.name}</h3>
                        <ArrowUpRight className="text-zinc-500 group-hover:text-neon transition-colors" size={18} />
                      </div>
                      <p className="mt-1 text-xs sm:text-sm text-zinc-400">{h.tagline}</p>
                      <div className="mt-3 flex items-center gap-2 text-[11px] text-zinc-500">
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full"
                          style={{ background: h.accent }}
                        />
                        <span>{formatLastUpdated(h.last_updated, isDE) || (isDE ? "Live-Guide" : "Live guide")}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </PageContainer>
    </AppShell>
  );
}

function formatLastUpdated(iso, isDE) {
  if (!iso) return null;
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    const prefix = isDE ? "Aktualisiert" : "Updated";
    if (diff < 60) return `${prefix} ${isDE ? "gerade eben" : "just now"}`;
    if (diff < 3600) return `${prefix} ${isDE ? `vor ${Math.floor(diff / 60)} Min` : `${Math.floor(diff / 60)} min ago`}`;
    if (diff < 86400) return `${prefix} ${isDE ? `vor ${Math.floor(diff / 3600)} Std` : `${Math.floor(diff / 3600)} h ago`}`;
    return `${prefix} ${isDE ? `vor ${Math.floor(diff / 86400)} Tg` : `${Math.floor(diff / 86400)} d ago`}`;
  } catch (_e) {
    return null;
  }
}
